import { eventBus, isShowLoader, useApp, useAppSelector, useTranslation } from '@libs/core';
import { SystemEventTypeEnum } from '@libs/types';
import React, { useEffect, useRef } from 'react';
import { probeConnectivity, TConnectivityVerdict } from '../common/connectivityProbe';
import OfflinePopup from '../common/OfflinePopup';
import { buildSlowConnectionToast, SLOW_FEEDBACK_COOLDOWN_MS } from '../common/SlowConnection';

type TSlowRequestGuardParams = {
    isOnline: boolean;
    /** Recovery handler for the popup button. */
    onRetry: () => void;
};

/**
 * Escalating feedback for a request that hangs.
 *
 * ```
 * request on the wire longer than 1500 ms (isLoading === true)
 *   → connectivity probe starts at 1300 ms
 *   → toast "slow connection" (verdict-dependent) fires at 1500 ms, or as soon as probe resolves (at most 1800 ms)
 *   → popup after another 5000 ms (6500 ms total)
 * request settles → probe aborted, timers cleared
 * ```
 */
export const useSlowRequestGuard = ({ isOnline, onRetry }: TSlowRequestGuardParams) => {
    const { setPopup } = useApp();
    const isLoading = useAppSelector(isShowLoader);
    const { t } = useTranslation();
    // Timestamps of the last toast / popup, each used for its own cooldown.
    const slowToastShownAt = useRef(0);
    const slowPopupShownAt = useRef(0);

    useEffect(() => {
        if (!isLoading || !isOnline) {
            return;
        }

        let isAborted = false;
        let probeVerdict: TConnectivityVerdict | null = null;
        const abortController = new AbortController();

        const probeTimer = setTimeout(() => {
            const probePromise = probeConnectivity(undefined, abortController.signal)
                .then((verdict) => {
                    probeVerdict = verdict;
                    return verdict;
                })
                .catch((e) => {
                    console.error('Connectivity probe failed:', e);
                    return 'unreachable' as TConnectivityVerdict;
                });

            const delayPromise = new Promise((resolve) => setTimeout(resolve, 200));

            Promise.all([probePromise, delayPromise]).then(([verdict]) => {
                if (isAborted) {
                    return;
                }
                const now = Date.now();
                if (now - slowToastShownAt.current >= SLOW_FEEDBACK_COOLDOWN_MS) {
                    slowToastShownAt.current = now;
                    const messageKey = verdict === 'online' ? 'internet.slow.ps.connection.message' : 'internet.slow.connection.message';
                    eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, buildSlowConnectionToast(t(messageKey)));
                }
            });
        }, 1300);

        const popupTimer = setTimeout(() => {
            const now = Date.now();
            if (now - slowPopupShownAt.current >= SLOW_FEEDBACK_COOLDOWN_MS) {
                slowPopupShownAt.current = now;
                const isSlowService = probeVerdict === 'online';
                setPopup(<OfflinePopup variant={isSlowService ? 'slowService' : 'offline'} onRetry={isSlowService ? () => setPopup(null) : onRetry} />);
            }
        }, 6500);

        return () => {
            isAborted = true;
            clearTimeout(probeTimer);
            clearTimeout(popupTimer);
            abortController.abort();
        };
    }, [isLoading, isOnline, onRetry, setPopup, t]);
};
